import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getAcademicYears, getCourseAndClass } from "../../Services";

// Thunk: fetch shared reference data used across fees pages
export const fetchReferenceData = createAsyncThunk(
  "fees/fetchReferenceData",
  async (_, { rejectWithValue }) => {
    try {
      const [coursesRes, yearsRes] = await Promise.all([
        getCourseAndClass(),
        getAcademicYears(),
      ]);

      // Handle services that return a string on error
      const coursesOk =
        coursesRes && typeof coursesRes === "object" && !coursesRes.error;
      const yearsOk =
        yearsRes && typeof yearsRes === "object" && !yearsRes.error;

      if (!coursesOk) {
        return rejectWithValue(
          (typeof coursesRes === "object" && coursesRes?.error) ||
            (typeof coursesRes === "string"
              ? coursesRes
              : "Failed to load courses/classes")
        );
      }
      if (!yearsOk) {
        return rejectWithValue(
          (typeof yearsRes === "object" && yearsRes?.error) ||
            (typeof yearsRes === "string"
              ? yearsRes
              : "Failed to load academic years")
        );
      }

      return {
        courses: coursesRes?.data?.courses || [],
        classes: coursesRes?.data?.classes || [],
        academicYears: yearsRes?.data?.academicYears || [],
      };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to load reference data");
    }
  }
);

const initialState = {
  reference: {
    courses: [],
    classes: [],
    academicYears: [],
    loading: false,
    loaded: false,
    error: null,
  },
};

const feesSlice = createSlice({
  name: "fees",
  initialState,
  reducers: {
    clearReferenceError: (state) => {
      state.reference.error = null;
    },
    // Allow manual retry by resetting error state
    retryReferenceData: (state) => {
      state.reference.error = null;
      state.reference.loaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferenceData.pending, (state) => {
        state.reference.loading = true;
        state.reference.error = null;
      })
      .addCase(fetchReferenceData.fulfilled, (state, action) => {
        state.reference.loading = false;
        state.reference.loaded = true;
        state.reference.courses = action.payload.courses;
        state.reference.classes = action.payload.classes;
        state.reference.academicYears = action.payload.academicYears;
      })
      .addCase(fetchReferenceData.rejected, (state, action) => {
        state.reference.loading = false;
        state.reference.error = action.payload || "Unable to load data";
      });
  },
});

// Actions
export const { clearReferenceError, retryReferenceData } = feesSlice.actions;

// Selectors
export const selectFeesReference = (state) => state.fees.reference;
export const selectAcademicYears = (state) =>
  state.fees.reference.academicYears;
export const selectCourses = (state) => state.fees.reference.courses;
export const selectClasses = (state) => state.fees.reference.classes;
export const selectReferenceLoaded = (state) => state.fees.reference.loaded;
export const selectReferenceLoading = (state) => state.fees.reference.loading;

export default feesSlice.reducer;
